import type {
  PriceCheckView,
  PriceMapView,
  UpsertPriceRuleRequest,
  UpsertPriceRuleResult,
} from "@planerius/contracts/cabinet"

import { apiRequest } from "./http"

/**
 * Динамические цены (3-Б10/Б11). Карта и проверка считаются сервером тем же выбором
 * правила, что слоты и запись: кабинет цену не вычисляет никогда — иначе карта
 * разошлась бы с кассой на первом же правиле «при равном приоритете — более узкое».
 */

export interface PriceRuleView {
  readonly id: string
  readonly name: string | null
  readonly scope: "service" | "category" | "employee" | "branch"
  readonly targetId: string
  readonly weekdayMask: number
  readonly timeFrom: string | null
  readonly timeTo: string | null
  readonly dateFrom: string | null
  readonly dateTo: string | null
  readonly mode: "percent" | "fixed"
  readonly percent: number | null
  readonly fixedPriceMinor: number | null
  readonly priority: number
  readonly isActive: boolean
}

type Row = Readonly<Record<string, unknown>>

function text(row: Row, key: string): string | null {
  const value = row[key]
  return typeof value === "string" && value !== "" ? value : null
}

function number(row: Row, key: string): number | null {
  const value = row[key]
  if (typeof value === "number") return value
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) return Number(value)
  return null
}

/** «10:00:00» из базы → «10:00», как ждёт контракт. */
function hhmm(value: string | null): string | null {
  return value === null ? null : value.slice(0, 5)
}

export function normalizePriceRules(body: unknown): readonly PriceRuleView[] {
  if (!Array.isArray(body)) return []
  return body
    .filter((row): row is Row => typeof row === "object" && row !== null)
    .map((row) => ({
      id: text(row, "id") ?? "",
      name: text(row, "name"),
      scope: (text(row, "scope") ?? "service") as PriceRuleView["scope"],
      targetId: text(row, "target_id") ?? "",
      weekdayMask: number(row, "weekday_mask") ?? 127,
      timeFrom: hhmm(text(row, "time_from")),
      timeTo: hhmm(text(row, "time_to")),
      dateFrom: text(row, "date_from"),
      dateTo: text(row, "date_to"),
      mode: (text(row, "mode") ?? "percent") as PriceRuleView["mode"],
      percent: number(row, "percent"),
      fixedPriceMinor: number(row, "fixed_price_minor"),
      priority: number(row, "priority") ?? 0,
      isActive: row["is_active"] === true,
    }))
}

export async function fetchPriceRules(signal?: AbortSignal): Promise<readonly PriceRuleView[]> {
  return normalizePriceRules(await apiRequest<unknown>("/cabinet/catalog/price-rules", { signal }))
}

export function upsertPriceRule(request: UpsertPriceRuleRequest): Promise<UpsertPriceRuleResult> {
  return apiRequest<UpsertPriceRuleResult>("/cabinet/catalog/price-rules", { method: "POST", body: request })
}

export function fetchPriceMap(
  query: { readonly serviceId: string; readonly branchId: string; readonly employeeId: string | null; readonly weekStart: string },
  signal?: AbortSignal,
): Promise<PriceMapView> {
  const params = new URLSearchParams({ serviceId: query.serviceId, branchId: query.branchId, weekStart: query.weekStart })
  if (query.employeeId !== null) params.set("employeeId", query.employeeId)
  return apiRequest<PriceMapView>(`/cabinet/catalog/price-map?${params.toString()}`, { signal })
}

export function fetchPriceCheck(
  query: { readonly serviceId: string; readonly branchId: string; readonly employeeId: string | null; readonly at: string },
  signal?: AbortSignal,
): Promise<PriceCheckView> {
  const params = new URLSearchParams({ serviceId: query.serviceId, branchId: query.branchId, at: query.at })
  if (query.employeeId !== null) params.set("employeeId", query.employeeId)
  return apiRequest<PriceCheckView>(`/cabinet/catalog/price-check?${params.toString()}`, { signal })
}
